export type ContentEmbedKind =
  | "tiktok"
  | "instagram"
  | "youtube"
  | "unknown";

export interface ContentEmbed {
  kind: ContentEmbedKind;
  embedSrc?: string; // present only when the URL maps to an embeddable post
}

function extractYouTubeId(url: URL, host: string, path: string): string | null {
  if (host === "youtu.be") return path.split("/").filter(Boolean)[0] ?? null;
  const v = url.searchParams.get("v");
  if (v) return v;
  const m = path.match(/\/(?:shorts|embed|v|live)\/([A-Za-z0-9_-]+)/);
  return m ? (m[1] ?? null) : null;
}

// Maps a creator-submitted content URL to a same-origin iframe embed when possible.
export function deriveContentEmbed(rawUrl: string): ContentEmbed {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return { kind: "unknown" };
  }

  const host = url.hostname.replace(/^www\./, "").toLowerCase();
  const path = url.pathname;

  if (host === "tiktok.com" || host.endsWith(".tiktok.com")) {
    const m = path.match(/\/video\/(\d+)/);
    return m
      ? { kind: "tiktok", embedSrc: `https://www.tiktok.com/player/v1/${m[1]}` }
      : { kind: "tiktok" };
  }

  if (
    host === "youtube.com" ||
    host.endsWith(".youtube.com") ||
    host === "youtu.be"
  ) {
    const id = extractYouTubeId(url, host, path);
    return id
      ? { kind: "youtube", embedSrc: `https://www.youtube.com/embed/${id}` }
      : { kind: "youtube" };
  }

  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    const m = path.match(/\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
    return m
      ? {
          kind: "instagram",
          embedSrc: `https://www.instagram.com/${m[1]}/${m[2]}/embed`,
        }
      : { kind: "instagram" };
  }

  return { kind: "unknown" };
}
